// src/services/AuthService.js

import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  sendEmailVerification,
  GoogleAuthProvider, 
  signInWithPopup,
  PhoneAuthProvider,
  RecaptchaVerifier,
  signInWithPhoneNumber
} from "firebase/auth";
import { doc, setDoc, getDoc, Timestamp } from "firebase/firestore";
import { auth, db } from "../firebase";

// User roles
export const USER_ROLES = {
  CUSTOMER: "customer",
  ADMIN: "admin",
  PHARMACIST: "pharmacist"
};

// Email & Password Authentication

// Register a new user
export const registerWithEmail = async (email, password, name, phone) => {
  try {
    // Create user in Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Update profile with name
    await updateProfile(user, { displayName: name });
    
    // Send email verification
    await sendEmailVerification(user);
    
    // Create user document in Firestore with role
    await setDoc(doc(db, "users", user.uid), {
      uid: user.uid,
      name: name,
      email: email,
      phone: phone,
      role: USER_ROLES.CUSTOMER, // Default role for registration
      createdAt: Timestamp.now()
    });
    
    return user;
  } catch (error) {
    console.error("Registration error:", error);
    throw error;
  }
};

// Login with email and password
export const loginWithEmail = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) {
    console.error("Login error:", error);
    throw error;
  }
};

// Get current user data with role
export const getCurrentUserData = async () => {
  const user = auth.currentUser;
  if (!user) return null;
  
  try {
    const userDoc = await getDoc(doc(db, "users", user.uid));
    if (userDoc.exists()) {
      return {
        ...user,
        ...userDoc.data()
      };
    }
    return user;
  } catch (error) {
    console.error("Error getting user data:", error);
    return user;
  }
};

// Check if current user is admin
export const isUserAdmin = async () => {
  try {
    const userData = await getCurrentUserData();
    return userData?.role === USER_ROLES.ADMIN || userData?.role === USER_ROLES.PHARMACIST;
  } catch (error) {
    console.error("Error checking admin status:", error);
    return false;
  }
};

// Reset password
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return true;
  } catch (error) {
    console.error("Password reset error:", error);
    throw error;
  }
};

// Sign out
export const logout = async () => {
  try {
    await signOut(auth);
    return true;
  } catch (error) {
    console.error("Logout error:", error);
    throw error;
  }
};

// Google Authentication
export const signInWithGoogle = async () => {
  try {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    
    // Check if user document exists, if not create one
    const userRef = doc(db, "users", result.user.uid);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      await setDoc(userRef, {
        uid: result.user.uid,
        name: result.user.displayName,
        email: result.user.email,
        phone: result.user.phoneNumber || "",
        role: USER_ROLES.CUSTOMER,
        createdAt: Timestamp.now()
      });
    }
    
    return result.user;
  } catch (error) {
    console.error("Google sign-in error:", error);
    throw error;
  }
};

// Phone Authentication
export const setupRecaptcha = (phoneNumber) => {
  window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
    'size': 'invisible'
  });
  return window.recaptchaVerifier;
};

export const requestOTP = async (phoneNumber) => {
  try {
    const recaptchaVerifier = setupRecaptcha();
    const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
    window.confirmationResult = confirmationResult;
    return confirmationResult;
  } catch (error) {
    console.error("OTP request error:", error);
    throw error;
  }
};

export const verifyOTP = async (otp) => {
  try {
    const result = await window.confirmationResult.confirm(otp);
    
    // Check if user document exists, if not create one
    const userRef = doc(db, "users", result.user.uid);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      await setDoc(userRef, {
        uid: result.user.uid,
        name: result.user.displayName || "Phone User",
        email: result.user.email || "",
        phone: result.user.phoneNumber,
        role: USER_ROLES.CUSTOMER,
        createdAt: Timestamp.now()
      });
    }
    
    return result.user;
  } catch (error) {
    console.error("OTP verification error:", error);
    throw error;
  }
};

// Auth state listener
export const onAuthStateChanged = (callback) => {
  return auth.onAuthStateChanged(async (user) => {
    if (user) {
      const userData = await getCurrentUserData();
      callback(userData);
    } else {
      callback(null);
    }
  });
};